import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";
import { getProduct } from "@/data/products";
import { isDaily, rentalDays } from "@/lib/dates";

const AppContext = createContext(null);

const CART_KEY = "cea30_cart";
const LANG_KEY = "cea30_lang";

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "fr");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { localStorage.setItem(LANG_KEY, lang); }, [lang]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const t = translations[lang];
  const toggleLang = useCallback(() => setLang((l) => (l === "fr" ? "en" : "fr")), []);

  const addToCart = useCallback((id, quantity = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) return prev.map((i) => (i.id === id ? { ...i, quantity: i.quantity + quantity } : i));
      return [...prev, { id, quantity }];
    });
  }, []);

  const setRental = useCallback((id, start, end) => {
    const days = start && end ? rentalDays(start, end) : 0;
    setCart((prev) => {
      const line = { id, start, end, quantity: Math.max(1, days) };
      return prev.some((i) => i.id === id) ? prev.map((i) => (i.id === id ? line : i)) : [...prev, line];
    });
  }, []);

  const setQuantity = useCallback((id, quantity) => {
    setCart((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, quantity) } : i)));
  }, []);

  const removeFromCart = useCallback((id) => setCart((prev) => prev.filter((i) => i.id !== id)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((s, i) => s + (isDaily(getProduct(i.id)) ? 1 : i.quantity), 0);

  const value = { lang, setLang, toggleLang, t, cart, addToCart, setRental, setQuantity, removeFromCart, clearCart, cartCount, cartOpen, setCartOpen };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const formatEUR = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);
